"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FeaturedProjects() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const projects = [{
    number: "\n",
    name: "Collegeville Estate Renovation",
    description: "Complete structural restoration and modern interior redesign for a historic property.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/estate_renovation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/estate_renovation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/estate_renovation.png-wvc-sizes",
    aspect: "aspect-[4/5]",
    colSpan: "lg:col-start-1 lg:col-end-6",
    offset: "lg:mt-0"
  }, {
    number: "\n",
    name: "Modern Custom Build",
    description: "Precision-engineered residential construction featuring sustainable materials and open-concept design.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/custom_build.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/custom_build.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/custom_build.png-wvc-sizes",
    aspect: "aspect-[16/9]",
    colSpan: "lg:col-start-7 lg:col-end-13",
    offset: "lg:mt-20"
  }, {
    number: "\n",
    name: "Luxury Property Management",
    description: "Comprehensive stewardship and maintenance services for high-end residential estates.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/property_management.png-wvc-sizes",
    aspect: "aspect-[4/5]",
    colSpan: "lg:col-start-2 lg:col-end-7",
    offset: "lg:mt-32"
  }, {
    number: "",
    name: "Architectural Additions",
    description: "Seamless integration of new living spaces that honor the original structural integrity.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/architectural_addition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/architectural_addition.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/architectural_addition.png-wvc-sizes",
    aspect: "aspect-[16/9]",
    colSpan: "lg:col-start-8 lg:col-end-13",
    offset: "lg:mt-16"
  }, {
    number: "\n",
    name: "Custom Millwork & Finishes",
    description: "Bespoke cabinetry and fine carpentry work tailored to unique client specifications.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/custom_millwork.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/custom_millwork.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/custom_millwork.webp-wvc-sizes",
    aspect: "aspect-[4/5]",
    colSpan: "lg:col-start-1 lg:col-end-6",
    offset: "lg:mt-28"
  }, {
    number: "",
    name: "Commercial Site Development",
    description: "Professional construction management for complex commercial and multi-use sites.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/commercial_site.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/commercial_site.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/commercial_site.png-wvc-sizes",
    aspect: "aspect-[16/9]",
    colSpan: "lg:col-start-7 lg:col-end-13",
    offset: "lg:mt-20"
  }];
  return <section data-section-id="4480" id="featured-projects" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="lg:col-span-5">
          <p className="font-default text-xs uppercase tracking-widest text-muted-foreground mb-8" style={{
          fontFamily: "var(--font-mono)"
        }}>Featured Projects</p>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground" style={{
          fontFamily: "var(--font-serif)"
        }}>
            Construction built
            <br />
            with{" "}
            <span className="italic" style={{
            fontFamily: "var(--font-serif)"
          }}>
              absolute precision
            </span>
            .
          </h2>
        </div>

        <div className="lg:col-start-7 lg:col-end-13 flex flex-col justify-end">
          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mb-8 max-w-md">
            At Moscariello Construction, every project is a testament to our commitment to structural integrity, refined aesthetics, and the enduring legacy of our craftsmanship.
          </p>
          <Link to="/ProjectGalleryPage" className="font-default text-xs uppercase tracking-widest text-foreground inline-flex items-center gap-2 group w-fit">
            <span style={{
            fontFamily: "var(--font-mono)"
          }}>View all projects</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-16 md:gap-y-20 gap-x-6">
        {projects.map((project, i) => <ProjectCard key={i} data-index={i} project={project} />)}
      </div>

      <div className="mt-28 md:mt-36">
        <Separator className="bg-border/60" />
        <div className="flex justify-center pt-12">
          <Link to="/ProjectGalleryPage" className="font-default text-xs uppercase tracking-widest text-foreground inline-flex items-center gap-2 group">
            <span style={{
            fontFamily: "var(--font-mono)"
          }}>See the full portfolio</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}
type Project = {
  number: string;
  name: string;
  description: string;
  src: string;
  srcset: string;
  sizes: string;
  aspect: string;
  colSpan: string;
  offset: string;
};
function ProjectCard({
  project,
  ...rest
}: {
  project: Project;
} & React.HTMLAttributes<HTMLDivElement>) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={ref} className={`${project.colSpan} ${project.offset} group transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className={`relative overflow-hidden ${project.aspect} bg-muted`}>
        <img src={project.src} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" style={{
        filter: "saturate(0.95) contrast(1.02)"
      }} />

        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out">
          <div className="bg-card text-card-foreground px-6 py-5 md:px-8 md:py-6 border-t border-border/40">
            <div className="flex items-baseline justify-between gap-4 mb-2">
              <h3 className="font-default text-xl md:text-2xl leading-tight text-card-foreground" style={{
              fontFamily: "var(--font-serif)"
            }}>
                {project.name}
              </h3>
              <span className="font-default text-xs uppercase tracking-widest text-muted-foreground shrink-0" style={{
              fontFamily: "var(--font-mono)"
            }}>
                {project.number}
              </span>
            </div>
            <p className="font-default text-sm leading-relaxed text-muted-foreground">
              {project.description}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-4">
        <span className="font-default text-[10px] uppercase tracking-widest text-muted-foreground" style={{
        fontFamily: "var(--font-mono)"
      }}>
          {project.number}
        </span>
        <span className="font-default text-sm italic text-foreground/70" style={{
        fontFamily: "var(--font-serif)"
      }}>
          {project.name}
        </span>
      </div>
    </div>;
}